// src/components/admin/adminsidebar/AdminSidebar.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import {
  FiGrid, FiUsers, FiAlertCircle, FiDollarSign,
  FiSettings, FiLogOut, FiChevronDown,
  FiChevronUp, FiAward,
  FiCreditCard, FiCalendar, FiMail, FiPlusCircle, FiBell, FiRefreshCcw
} from 'react-icons/fi';
import {
  FaTractor, FaClipboardList, FaLayerGroup,
  FaUserShield, FaBlog
} from "react-icons/fa";
import { Modal, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import AppContext from '../../../context/AppContext';
import '../../../assets/css/superadmin/sidebar/SideBar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedInAdmin, baseUrl } = useContext(AppContext);
  const isModerator = loggedInAdmin?.issuper === 'moderator';
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // ---- Unread notifications count ----
  const fetchUnreadCount = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !baseUrl) return;

    axios
      .post(
        `${baseUrl}/superadmin/notifications/getUnreadCount`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        if (res.data?.success) {
          setUnreadCount(Number(res.data.count) || 0);
        }
      })
      .catch(() => {
        // count na aaye to sidebar kharab na ho
      });
  }, [baseUrl]);

  useEffect(() => {
    // 1) page/route badalne par dobara load
    fetchUnreadCount();
  }, [fetchUnreadCount, location.pathname]);

  useEffect(() => {
    // 2) har 20 second baad dobara check (naye notification ke liye)
    const timer = setInterval(fetchUnreadCount, 20000);

    // 3) Notifications page read/delete kare to foran update
    const onUpdated = () => fetchUnreadCount();
    window.addEventListener('notifications-updated', onUpdated);

    return () => {
      clearInterval(timer);
      window.removeEventListener('notifications-updated', onUpdated);
    };
  }, [fetchUnreadCount]);

  const menu = [
    { label: 'Dashboard', icon: <FiGrid />, path: '/admin/dashboard' },
    // Sub-admin management sirf full superadmin ke liye
    ...(isModerator ? [] : [{ label: 'Sub-admin', icon: <FaUserShield />, path: '/admin/sub-admin' }]),
    { label: 'Equipment Category ', icon: <FaLayerGroup />, path: '/admin/category' },
    { label: 'Mechanic Specilization ', icon: <FiAward />, path: '/admin/specilization' },
    {
      label: 'User Management',
      icon: <FiUsers />,
      path: '/admin/users',
      hasSubmenu: true,
      submenu: [
        { label: 'Rentalshop Management', path: '/admin/users/rental-owners' },
        { label: 'Mechanic Management', path: '/admin/users/mechanics' },
        { label: 'Customer Management', path: '/admin/users/end-users' }
      ]
    },
    { label: 'Mechanic Maintenance Request', icon: <FaClipboardList />, path: '/admin/maintenance-request' },
    { label: 'Rental Booking', icon: <FiCalendar />, path: '/admin/bookings' },
    { label: 'payouts Request', icon: <FiCreditCard />, path: '/admin/payouts' },
    { label: 'Refund Requests', icon: <FiRefreshCcw />, path: '/admin/refunds' },
    { label: 'TopUp Requests', icon: <FiPlusCircle />, path: '/admin/topup' },
    { label: 'Blogs', icon: <FaBlog />, path: '/admin/blogs' },
    { label: 'Disputes', icon: <FiAlertCircle />, path: '/admin/disputes' },
    { label: 'Contact Form', icon: <FiMail />, path: '/admin/contactform' },
    { label: 'Earning', icon: <FiDollarSign />, path: '/admin/earning' },
    { label: 'Notifications', icon: <FiBell />, path: '/admin/notifications', badge: unreadCount },
    { label: 'Settings', icon: <FiSettings />, path: '/admin/settings' }
  ];

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminPassword');
    localStorage.removeItem('authToken');
    localStorage.removeItem('LoggedInAdmin');
    navigate('/admin/login');
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth <= 768 && onClose) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="smart-agri-sidebar-overlay" onClick={onClose}></div>
      )}

      <aside className={`smart-agri-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="smart-agri-sidebar-brand">
          <div className="smart-agri-sidebar-logo">
            <FaTractor size={32} />
          </div>
          <div className="smart-agri-sidebar-brand-text">
            <h3 className="smart-agri-sidebar-brand-title">Smart Agri</h3>
            <span className="smart-agri-sidebar-brand-subtitle">RENT</span>
          </div>
        </div>

        <nav className="smart-agri-sidebar-menu">
          {menu.map((item) => (
            <div key={item.path}>
              <button
                className={`smart-agri-sidebar-menu-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => {
                  if (item.hasSubmenu) {
                    handleUserMenuToggle();
                  } else {
                    handleNavigation(item.path);
                  }
                }}
              >
                <span className="smart-agri-menu-icon">{item.icon}</span>
                <span className="smart-agri-menu-label">{item.label}</span>

                {item.badge > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      minWidth: '22px',
                      height: '22px',
                      padding: '0 7px',
                      borderRadius: '11px',
                      background: '#FDB714',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}

                {item.hasSubmenu && (
                  <span className="smart-agri-menu-chevron">
                    {isUserMenuOpen ? <FiChevronUp /> : <FiChevronDown />}
                  </span>
                )}
              </button>

              {item.submenu && isUserMenuOpen && (
                <div className="smart-agri-sidebar-submenu">
                  {item.submenu.map((sub) => (
                    <button
                      key={sub.path}
                      className={`smart-agri-sidebar-submenu-item ${location.pathname === sub.path ? 'active' : ''}`}
                      onClick={() => handleNavigation(sub.path)}
                    >
                      <span className="smart-agri-submenu-dot">•</span>
                      <span>{sub.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <button className="smart-agri-sidebar-logout" onClick={handleLogoutClick}>
          <FiLogOut size={20} />
          <span>Logout</span>
        </button>
      </aside>

      <Modal
        show={showLogoutModal}
        onHide={handleLogoutCancel}
        centered
        className="smart-agri-logout-modal"
      >
        <Modal.Header closeButton className="smart-agri-logout-modal-header">
          <Modal.Title className="smart-agri-logout-modal-title">Confirm Logout</Modal.Title>
        </Modal.Header>
        <Modal.Body className="smart-agri-logout-modal-body">
          <div className="smart-agri-logout-icon-wrapper">
            <FiLogOut size={48} />
          </div>
          <p className="smart-agri-logout-message">Are you sure you want to logout from Admin Panel?</p>
        </Modal.Body>
        <Modal.Footer className="smart-agri-logout-modal-footer">
          <Button
            className="smart-agri-logout-cancel-btn"
            onClick={handleLogoutCancel}
          >
            Cancel
          </Button>
          <Button
            className="smart-agri-logout-confirm-btn"
            onClick={handleLogoutConfirm}
          >
            Yes, Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sidebar;